// Pure rules for delivery-location editing. Two correctness-sensitive decisions
// live here (and are unit-tested): how a removed location re-maps team access,
// and what address a "same as previous" row actually resolves to.

import { ApplicationForm, LocationEntry } from './types';

type Address = Pick<LocationEntry, 'line1' | 'line2' | 'city' | 'state' | 'zip'>;

// Remove location `idx` and re-map every team member's locationAccess. Access is
// stored as indices into form.locations, so without this a removal would silently
// repoint each member to whatever location slid into the vacated index.
export function removeLocationAt(
  form: ApplicationForm,
  idx: number
): Pick<ApplicationForm, 'locations' | 'teamMembers'> {
  const locations = form.locations.filter((_, i) => i !== idx);
  const teamMembers = form.teamMembers.map((m) => ({
    ...m,
    locationAccess: m.locationAccess.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i)),
  }));
  return { locations, teamMembers };
}

// The address a "same as previous" row mirrors: the main office for location 0,
// otherwise the prior location — following the chain so a run of mirrored rows
// resolves to the real source. Returns the row's own fields when not mirrored.
export function effectiveLocationAddress(form: ApplicationForm, i: number): Address {
  const loc = form.locations[i];
  if (!loc?.sameAsPrevious) {
    return { line1: loc.line1, line2: loc.line2, city: loc.city, state: loc.state, zip: loc.zip };
  }
  if (i === 0) {
    return {
      line1: form.officeLine1,
      line2: form.officeLine2,
      city: form.officeCity,
      state: form.officeState,
      zip: form.officeZip,
    };
  }
  return effectiveLocationAddress(form, i - 1);
}

export const sameAsPreviousLabel = (i: number): string =>
  i === 0 ? 'Same as main office address' : 'Same as previous location';
